import React, { useEffect, useState } from "react";
import { Panel } from "@/components/Panel";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { GitCompareArrows, X, Plus, Loader2, CheckCircle2, AlertTriangle, AlertCircle, MinusCircle, Sparkles, FileDown } from "lucide-react";
import { API } from "@/lib/api";

const STATUS = {
  pass: { cls: "text-[#00E676]", icon: CheckCircle2, label: "PASS" },
  warn: { cls: "text-[#FFB000]", icon: AlertTriangle, label: "WARN" },
  fail: { cls: "text-[#FF3B30]", icon: AlertCircle, label: "FAIL" },
  na:   { cls: "text-zinc-600", icon: MinusCircle, label: "N/A" },
};

const SCORE_COLOR = (score) =>
  score >= 85 ? "text-[#00E676]"
  : score >= 60 ? "text-[#FFB000]"
  : "text-[#FF3B30]";


/**
 * Phase 33C — Compare playbooks panel.
 * Lets user run the SAME data source against MULTIPLE country playbooks
 * and view the result side-by-side. Useful for multinational holdcos.
 */
export default function ComparePlaybooksPanel({ sourceId, defaultPlaybookId }) {
  const [playbooks, setPlaybooks] = useState([]);
  const [picked, setPicked] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summarising, setSummarising] = useState(false);

  useEffect(() => {
    api.get("/playbooks").then(r => setPlaybooks(r.data.items || [])).catch(() => {});
  }, []);

  // Default: preselect the auto-suggested playbook + an obvious peer
  useEffect(() => {
    if (defaultPlaybookId && picked.length === 0 && playbooks.length > 0) {
      setPicked([defaultPlaybookId]);
    }
  }, [defaultPlaybookId, playbooks, picked.length]);

  const togglePick = (pid) => {
    setPicked(prev => {
      if (prev.includes(pid)) return prev.filter(x => x !== pid);
      if (prev.length >= 6) {
        toast.error("Maximum 6 playbooks per comparison");
        return prev;
      }
      return [...prev, pid];
    });
  };

  const runCompare = async () => {
    if (picked.length < 2) {
      toast.error("Pick at least 2 playbooks to compare");
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      picked.forEach(p => params.append("playbook_ids", p));
      const r = await api.post(
        `/data-sources/${sourceId}/scorecard-compare?${params.toString()}`,
      );
      setComparison(r.data);
      setSummary(null);  // reset any prior summary on a fresh run
      toast.success(`Compared against ${picked.length} playbooks`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Comparison failed");
    }
    setLoading(false);
  };

  const summarise = async () => {
    if (!comparison?.rows?.length) return;
    setSummarising(true);
    try {
      const params = new URLSearchParams();
      picked.forEach(p => params.append("playbook_ids", p));
      const r = await api.post(
        `/data-sources/${sourceId}/scorecard-compare/summarise?${params.toString()}`,
      );
      setSummary(r.data.summary);
      toast.success("Strategic summary ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Summarise failed");
    }
    setSummarising(false);
  };

  const downloadPdf = async () => {
    try {
      const token = localStorage.getItem("dara_token");
      const resp = await fetch(
        `${API}/data-sources/${sourceId}/scorecard-compare/summary.pdf`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `DARA_Strategic_Summary_${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Strategic summary downloaded");
    } catch (e) {
      toast.error(`Download failed: ${e.message}`);
    }
  };

  if (!open) {
    return (
      <Panel
        testId="compare-playbooks-collapsed"
        title="// COMPARE ACROSS MARKETS"
        meta={<GitCompareArrows className="w-3.5 h-3.5 text-[#00BFFF]" />}
      >
        <div className="p-5 flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <p className="font-mono text-[11px] text-zinc-400 leading-relaxed">
              Run this source against multiple country playbooks side-by-side. Useful when one
              book crosses jurisdictions — e.g. holdco operating in both UAE and Saudi Arabia,
              or a SEPA company comparing France · Germany · Spain rule packs.
            </p>
            <p className="font-mono text-[10px] text-zinc-600 mt-2">
              Up to 6 playbooks per run · regulator-sourced · no LLM call.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="open-compare-btn"
            className="flex-shrink-0 border border-[#00BFFF]/40 text-[#00BFFF] px-4 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-[#00BFFF]/10 transition-colors flex items-center gap-2"
          >
            <GitCompareArrows className="w-3.5 h-3.5" /> Compare
          </button>
        </div>
      </Panel>
    );
  }

  return (
    <Panel
      testId="compare-playbooks-panel"
      title="// COMPARE ACROSS MARKETS"
      meta={
        <button
          type="button"
          onClick={() => { setOpen(false); setComparison(null); setPicked([]); }}
          data-testid="close-compare-btn"
          className="text-zinc-500 hover:text-white"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      }
    >
      <div className="p-5 space-y-5">
        {/* Picker */}
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
            STEP 1 · PICK 2–6 PLAYBOOKS
          </div>
          <div className="flex flex-wrap gap-2" data-testid="compare-picker">
            {playbooks.map(pb => {
              const active = picked.includes(pb.id);
              return (
                <button
                  key={pb.id}
                  type="button"
                  onClick={() => togglePick(pb.id)}
                  data-testid={`compare-pick-${pb.id}`}
                  className={`px-3 py-2 font-mono text-[10px] uppercase tracking-widest border transition-colors flex items-center gap-2 ${
                    active
                      ? "bg-[#00BFFF]/10 text-[#00BFFF] border-[#00BFFF]/50"
                      : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-white"
                  }`}
                  title={pb.description}
                >
                  {active ? <CheckCircle2 className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                  {pb.name}
                </button>
              );
            })}
          </div>
          <div className="font-mono text-[10px] text-zinc-600 mt-2">
            Picked: {picked.length} / 6
          </div>
        </div>

        <button
          type="button"
          onClick={runCompare}
          disabled={loading || picked.length < 2}
          data-testid="run-compare-btn"
          className="bg-[#00BFFF] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-2.5 hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitCompareArrows className="w-4 h-4" />}
          {loading ? "RUNNING…" : "RUN COMPARISON"}
        </button>

        {/* Result */}
        {comparison && (
          <div className="space-y-4 border-t border-zinc-900 pt-5" data-testid="compare-result">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              STEP 2 · RESULTS · {comparison.source_name}
            </div>

            {/* Score row */}
            <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${comparison.rows.length}, minmax(0, 1fr))` }}>
              {comparison.rows.map(r => (
                <div key={r.playbook_id} className="border border-zinc-900 p-3" data-testid={`compare-score-${r.playbook_id}`}>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-600 truncate">
                    {r.market} · {r.currency}
                  </div>
                  <div className={`font-heading font-black text-3xl tracking-tighter mt-1 ${SCORE_COLOR(r.overall_score)}`}>
                    {r.overall_score}
                  </div>
                  <div className="font-mono text-[9px] text-zinc-600 mt-1">
                    {r.applicable_rules}/{r.total_rules} applicable
                  </div>
                  <div className="font-mono text-[9px] text-zinc-700 mt-2 truncate" title={r.regulator}>
                    {r.regulator}
                  </div>
                </div>
              ))}
            </div>

            {/* Strategic summary (Phase 33D) */}
            <div className="border border-[#FFB000]/30 bg-[#FFB000]/[0.03] p-4" data-testid="compare-summary-block">
              {summary ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5 text-[#FFB000]" />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000]">
                      // STRATEGIC SUMMARY · DARA-GROUNDED
                    </span>
                  </div>
                  <div className="font-heading font-black text-base sm:text-lg tracking-tight text-white" data-testid="summary-headline">
                    {summary.headline}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3 mt-3 font-mono text-[11px] leading-relaxed">
                    {summary.strengths && (
                      <div className="border-l-2 border-[#00E676]/60 pl-3">
                        <div className="text-[9px] uppercase tracking-widest text-[#00E676] mb-1">STRENGTHS</div>
                        <div className="text-zinc-300">{summary.strengths}</div>
                      </div>
                    )}
                    {summary.weaknesses && (
                      <div className="border-l-2 border-[#FF3B30]/60 pl-3">
                        <div className="text-[9px] uppercase tracking-widest text-[#FF3B30] mb-1">WEAKNESSES</div>
                        <div className="text-zinc-300">{summary.weaknesses}</div>
                      </div>
                    )}
                  </div>
                  {summary.highest_leverage_fix && (
                    <div className="border-t border-zinc-800 pt-3 mt-3">
                      <div className="text-[9px] font-mono uppercase tracking-widest text-[#FFB000] mb-1">HIGHEST-LEVERAGE FIX</div>
                      <div className="font-mono text-[11px] text-white leading-relaxed">{summary.highest_leverage_fix}</div>
                    </div>
                  )}
                  {summary.expansion_play && (
                    <div className="border-t border-zinc-800 pt-3 mt-3">
                      <div className="text-[9px] font-mono uppercase tracking-widest text-[#00BFFF] mb-1">NEXT MARKET TO TARGET</div>
                      <div className="font-mono text-[11px] text-zinc-300 leading-relaxed">{summary.expansion_play}</div>
                    </div>
                  )}
                  <div className="border-t border-zinc-800 pt-3 mt-3 flex items-center justify-between gap-3 flex-wrap">
                    <div className="font-mono text-[10px] text-zinc-500">
                      Share with CFO / auditor / board
                    </div>
                    <button
                      type="button"
                      onClick={downloadPdf}
                      data-testid="download-strategic-summary-btn"
                      className="border border-[#FFB000]/40 text-[#FFB000] px-4 py-2 font-mono text-[10px] uppercase tracking-widest hover:bg-[#FFB000]/10 transition-colors flex items-center gap-2"
                    >
                      <FileDown className="w-3.5 h-3.5" />
                      Download Strategic Summary (PDF)
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                      // STRATEGIC SUMMARY
                    </div>
                    <p className="font-mono text-[11px] text-zinc-400 mt-1 leading-relaxed">
                      Ask DARA to distil this matrix into a 5-point exec summary —
                      strongest market, weakest, the single highest-leverage fix, and which adjacent market to target next.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={summarise}
                    disabled={summarising}
                    data-testid="summarise-comparison-btn"
                    className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-4 py-2.5 hover:opacity-90 disabled:opacity-40 flex items-center gap-2 transition-opacity"
                  >
                    {summarising ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    {summarising ? "ANALYSING…" : "SUMMARISE COMPARISON"}
                  </button>
                </div>
              )}
            </div>

            {/* Per-rule comparison table */}
            <div className="overflow-x-auto -mx-5 px-5">
              <table className="w-full font-mono text-[10px] border-collapse" data-testid="compare-table">
                <thead>
                  <tr className="border-b border-zinc-800">
                    <th className="text-left py-2 px-2 text-zinc-500 uppercase tracking-widest font-mono text-[9px] sticky left-0 bg-black z-10 min-w-[160px]">
                      RULE / CATEGORY
                    </th>
                    {comparison.rows.map(r => (
                      <th key={r.playbook_id} className="text-center py-2 px-2 text-zinc-500 uppercase tracking-widest font-mono text-[9px] min-w-[120px]">
                        {r.playbook_name.split(" ")[0]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Build union of rule ids across all rows */}
                  {(() => {
                    const seen = new Set();
                    const allRules = [];
                    comparison.rows.forEach(r => r.results.forEach(rule => {
                      if (!seen.has(rule.id)) {
                        seen.add(rule.id);
                        allRules.push({ id: rule.id, title: rule.title, category: rule.category });
                      }
                    }));
                    return allRules.map(rule => (
                      <tr key={rule.id} className="border-b border-zinc-900 hover:bg-zinc-900/30">
                        <td className="py-2 px-2 sticky left-0 bg-black z-10">
                          <div className="text-white text-[10px]">{rule.title}</div>
                          <div className="text-zinc-600 text-[9px]">{rule.category}</div>
                        </td>
                        {comparison.rows.map(row => {
                          const result = row.results.find(x => x.id === rule.id);
                          if (!result) {
                            return (
                              <td key={row.playbook_id} className="text-center py-2 px-2 text-zinc-700">
                                —
                              </td>
                            );
                          }
                          const s = STATUS[result.status] || STATUS.na;
                          const Icon = s.icon;
                          return (
                            <td key={row.playbook_id} className="text-center py-2 px-2">
                              <div className={`flex flex-col items-center gap-0.5 ${s.cls}`}>
                                <Icon className="w-3.5 h-3.5" />
                                <span className="text-[9px] font-mono">{s.label}</span>
                                {result.observed && (
                                  <span className="text-[8px] text-zinc-500 truncate max-w-[110px]" title={result.observed}>
                                    {result.observed}
                                  </span>
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>

            <div className="font-mono text-[10px] text-zinc-600 leading-relaxed border-l-2 border-[#00BFFF]/40 pl-3">
              Persisted to <span className="text-zinc-400">db.playbook_comparisons</span> for audit.
              Each cell shows the rule&apos;s status + observed metric for that market context.
              Cells with — mean the playbook didn&apos;t include that rule.
            </div>
          </div>
        )}
      </div>
    </Panel>
  );
}
